/**
 * Read-only dental findings and treatment plan for the prescription review and
 * visit-complete screens. Same wording as the prescription PDF (chartForPrint),
 * so what the dentist reviews is what prints.
 */
import React from "react";
import type { DentalChart } from "../../schemas/dental.schema";
import { chartForPrint } from "../../utils/dental-print";

interface Props {
  chart: DentalChart | null | undefined;
  /** Shown as "Edit on Diagnosis Pad" when given. */
  onEdit?: () => void;
}

export const DentalSummary: React.FC<Props> = ({ chart, onEdit }) => {
  const { findings, plan } = chartForPrint(chart);
  if (!findings.length && !plan.length) return null;
  return (
    <section aria-labelledby="dental-summary-title" className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 id="dental-summary-title" className="text-base font-bold text-slate-800">Dental findings and treatment plan</h3>
        {onEdit && (
          <button type="button" onClick={onEdit} className="text-xs font-semibold text-sky-700 hover:underline">
            Edit on Diagnosis Pad
          </button>
        )}
      </div>
      {findings.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm text-slate-700">
          {findings.map((f) => <li key={f}>{f}</li>)}
        </ul>
      )}
      {plan.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full min-w-[420px] text-left text-sm tabular-nums">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2">Tooth / area</th><th className="px-3 py-2">Procedure</th><th className="px-3 py-2">Sittings</th></tr>
            </thead>
            <tbody>
              {plan.map((p, i) => (
                <tr key={`${p.where}-${p.label}-${i}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-600">{p.where}</td>
                  <td className="px-3 py-2 text-slate-800">{p.label}</td>
                  <td className="px-3 py-2 text-slate-600">{p.sittings}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-500">Prints on the prescription. No prices.</p>
    </section>
  );
};

export default DentalSummary;
